import React from 'react';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { GridApi } from 'ag-grid-community';

interface RolesAutocompleteProps<T extends string = string> {
  roles: T[];
  selectedRoles: T[];
  setSelectedRoles: React.Dispatch<React.SetStateAction<T[]>>;
  gridApi?: GridApi | null;
  isInUserForm?: boolean;
}

export default function RolesAutocomplete<T extends string = string>({
  roles,
  selectedRoles,
  setSelectedRoles,
  gridApi,
  isInUserForm = false
}: RolesAutocompleteProps<T>) {
  const allRoles = roles;
  const isAllSelected = selectedRoles.length === allRoles.length;
  const handleChange = (event: React.SyntheticEvent, newValue: T[]) => {
    setSelectedRoles(newValue);
    if (gridApi && !isInUserForm) {
      const filterModel = gridApi.getFilterModel();
      if (newValue.length > 0 && newValue.length < allRoles.length) {
        gridApi.setFilterModel({
          ...filterModel,
          roles: {
            filterType: 'set',
            values: newValue
          }
        });
      } else {
        const { roles: removed, ...rest } = filterModel;
        gridApi.setFilterModel(rest);
      }
      gridApi.onFilterChanged();
    }
  };

  const options = isAllSelected ? allRoles : allRoles.filter((role) => !selectedRoles.includes(role));

  return (
    <>
      {!isInUserForm && (
        <Typography variant="caption" color="secondary">
          Roles
        </Typography>
      )}
      <Autocomplete<T, true, false, false>
        multiple
        id="rolesFilter"
        options={options}
        value={isAllSelected ? [] : selectedRoles}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" size="small" placeholder={selectedRoles.length > 0 ? '' : 'Select Roles'} />
        )}
        sx={{
          flexGrow: 1,
          minWidth: 200,
          maxWidth: 600,
          ...(isInUserForm && {
            '& .MuiOutlinedInput-root': {
              height: 'auto',
              flexWrap: 'wrap',
              alignItems: 'center'
            }
          }),
          ...(!isInUserForm && {
            '& .MuiOutlinedInput-root': {
              height: 32.5,
              whiteSpace: 'nowrap',
              flexWrap: 'nowrap'
            }
          }),

          '& .MuiChip-root': {
            height: 20,
            fontSize: '10px'
          },
          '& .MuiAutocomplete-input': {
            padding: '0 4px'
          },
          '& .MuiChip-deleteIcon, & .MuiAutocomplete-clearIndicator svg': {
            fontSize: 17,
            width: 17,
            height: 17
          }
        }}
      />
    </>
  );
}
